import { useState, useEffect, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, ShoppingCart, Package, AlertCircle, Truck, Shield, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useRegion } from "@/components/RegionContext";
import { useLanguage } from "@/components/LanguageContext";
import { createPageUrl } from "@/utils";
import { trackInitiateCheckout } from "@/lib/pixel";
import { base44 } from "@/api/base44Client";

// WhatsApp business number (matches the floating WhatsApp button in the app layout)
const WHATSAPP_NUMBER = "8618218291826";

const PRODUCT_PRICE = 399; // USD baseline (matches the Stripe checkout price)

export default function CODCheckoutPage() {
  const { region, currency, formatLocalPrice } = useRegion();
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();

  const isMiddleEast = region === "uae" || region === "sa";

  const [quantity, setQuantity] = useState(() => {
    const q = Number(searchParams.get("quantity"));
    return Number.isFinite(q) && q >= 1 ? q : 1;
  });

  const [formData, setFormData] = useState({
    customer_name: searchParams.get("customer_name") || "",
    email: searchParams.get("email") || "",
    phone: searchParams.get("phone") || "",
    shipping_address: searchParams.get("shipping_address") || "",
    city: searchParams.get("city") || "",
    state: searchParams.get("state") || "",
    zip_code: searchParams.get("zip_code") || "",
    country: searchParams.get("country") || (region === "sa" ? "Saudi Arabia" : "United Arab Emirates"),
    order_notes: searchParams.get("order_notes") || "",
  });

  const [paymentError, setPaymentError] = useState(null);
  const [formError, setFormError] = useState(null);

  // If region changes after mount (IP detection finished late) and the
  // customer hasn't customized the country, follow the detected region.
  useEffect(() => {
    setFormData((prev) => {
      if (["United Arab Emirates", "Saudi Arabia"].includes(prev.country)) {
        return { ...prev, country: region === "sa" ? "Saudi Arabia" : "United Arab Emirates" };
      }
      return prev;
    });
  }, [region]);

  // Pick up quantity changes that arrive after first render (Customer changes
  // the quantity selector on the Product page, then clicks the COD button.)
  useEffect(() => {
    const q = Number(searchParams.get("quantity"));
    if (Number.isFinite(q) && q >= 1) setQuantity(q);
  }, [searchParams]);

  const totalAmount = PRODUCT_PRICE * quantity;

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormError(null);
  };

  const validate = () => {
    const required = ["customer_name", "email", "phone", "shipping_address", "city", "state", "zip_code", "country"];
    for (const field of required) {
      if (!String(formData[field] || "").trim()) {
        return "Please fill in all required fields, including your full contact information and shipping address, before sending your COD order.";
      }
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return "Please enter a valid email address.";
    }
    return null;
  };

  const buildWhatsAppMessage = () => {
    const lines = [
      language === "ar" ? "مرحبًا LCTX، أرغب بطلب الدفع عند الاستلام (COD):" : "Hello LCTX, I'd like to place a COD order (Cash on Delivery):",
      "",
      `Product: LCTX Tyre Changer`,
      `Quantity: ${quantity}`,
      `Total: ${formatLocalPrice(totalAmount)} (${currency.code}) - ${t("cod_payment")}`,
      "",
      language === "ar" ? "معلومات العميل:" : "Customer Information:",
      `${language === "ar" ? "الاسم" : "Name"}: ${formData.customer_name}`,
      `${language === "ar" ? "البريد" : "Email"}: ${formData.email}`,
      `${language === "ar" ? "الهاتف" : "Phone"}: ${formData.phone}`,
      "",
      language === "ar" ? "عنوان الشحن:" : "Shipping Address:",
      formData.shipping_address,
      `${formData.city}, ${formData.state}`,
      `${formData.zip_code}, ${formData.country}`,
    ];
    if (formData.order_notes && formData.order_notes.trim()) {
      lines.push("", `${language === "ar" ? "ملاحظات" : "Notes"}: ${formData.order_notes}`);
    }
    return lines.join("\n");
  };

  const handleSendWhatsApp = (e) => {
    e.preventDefault();
    const error = validate();
    if (error) {
      setFormError(error);
      setPaymentError(error);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      if (window.self !== window.top) {
        setPaymentError("Checkout works only from the published app. Please publish your app and open it in a new tab to complete your COD order.");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
    } catch (_) {
      setPaymentError("Checkout works only from the published app. Please publish your app and open it in a new tab to complete your COD order.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setFormError(null);
    setPaymentError(null);

    // Meta conversion signal + analytics: same intent as the Stripe checkout.
    trackInitiateCheckout(quantity, { value: totalAmount * currency.rate, currency: currency.code });
    base44.analytics.track({
      eventName: "cod_order_initiated",
      properties: {
        quantity,
        total_usd: totalAmount,
        source: "cod_checkout_page",
      },
    });

    const message = buildWhatsAppMessage();
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const summaryLines = useMemo(() => [
    { label: language === "ar" ? "السعر" : `Price (${currency.en})`, value: formatLocalPrice(PRODUCT_PRICE) },
    ...(isMiddleEast ? [] : [{ label: "GST (Included)", value: formatLocalPrice(totalAmount / 11) }]),
    { label: language === "ar" ? "الشحن" : "Shipping", value: language === "ar" ? "مجاني" : "FREE", className: "text-green-600 font-bold" },
  ], [language, currency, formatLocalPrice, isMiddleEast, totalAmount]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back to product */}
        <div className="mb-6">
          <Link to={createPageUrl("Product")} className="text-sm text-green-700 hover:text-green-800 font-semibold inline-flex items-center gap-2">
            ← {language === "ar" ? "العودة إلى المنتج" : "Back to Product"}
          </Link>
        </div>

        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <div className="inline-flex items-center gap-3 bg-[#25D366] text-white px-5 py-2 rounded-full mb-4 shadow-lg">
            <MessageCircle className="w-5 h-5" />
            <span className="font-bold text-sm">{t("cod_payment")} · WhatsApp</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-3">
            {language === "ar" ? "الدفع عند الاستلام" : "Cash on Delivery"}
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            {language === "ar"
              ? "املأ بياناتك أدناه وسنرسل لك تأكيد الطلب عبر واتساب. ادفع نقدًا عند استلام طردك."
              : "Fill in your details below and we'll confirm your order on WhatsApp. Pay with cash when your parcel arrives."}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card className="shadow-2xl border-0">
              <CardHeader className="bg-gradient-to-r from-green-600 to-green-700 text-white p-8">
                <CardTitle className="flex items-center gap-3 text-2xl md:text-3xl font-black">
                  <Package className="w-7 h-7 md:w-8 md:h-8" />
                  {language === "ar" ? "معلومات الطلب" : "Order Information"}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 md:p-8">
                {(formError || paymentError) && (
                  <Alert className="mb-6 bg-red-50 border-red-300 border-2">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <AlertDescription className="text-red-900 font-semibold">{formError || paymentError}</AlertDescription>
                  </Alert>
                )}

                <form className="space-y-6" onSubmit={handleSendWhatsApp}>
                  {/* Contact Information */}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {language === "ar" ? "معلومات التواصل" : "Contact Information"}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="customer_name">
                          {language === "ar" ? "الاسم الكامل *" : "Full Name *"}
                        </Label>
                        <Input
                          id="customer_name"
                          value={formData.customer_name}
                          onChange={(e) => handleInputChange("customer_name", e.target.value)}
                          placeholder={language === "ar" ? "محمد علي" : "John Doe"}
                          required
                          className="h-12"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">{language === "ar" ? "البريد الإلكتروني *" : "Email *"}</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          placeholder={language === "ar" ? "name@example.com" : "john@example.com"}
                          required
                          className="h-12"
                        />
                      </div>
                    </div>
                    <div className="space-y-2 mt-6">
                      <Label htmlFor="phone">{language === "ar" ? "الهاتف *" : "Phone *"}</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                        placeholder={region === "uae" ? "+971 50 000 0000" : region === "sa" ? "+966 50 000 0000" : "+1 555 000 0000"}
                        required
                        className="h-12"
                      />
                    </div>
                  </div>

                  {/* Shipping Address */}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {language === "ar" ? "عنوان الشحن" : "Shipping Address"}
                    </h3>
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="shipping_address">
                          {language === "ar" ? "العنوان *" : "Street Address *"}
                        </Label>
                        <Input
                          id="shipping_address"
                          value={formData.shipping_address}
                          onChange={(e) => handleInputChange("shipping_address", e.target.value)}
                          placeholder={language === "ar" ? "123 الشارع الرئيسي" : "123 Main Street"}
                          required
                          className="h-12"
                        />
                      </div>
                      <div className="grid md:grid-cols-3 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="city">{language === "ar" ? "المدينة *" : "City *"}</Label>
                          <Input
                            id="city"
                            value={formData.city}
                            onChange={(e) => handleInputChange("city", e.target.value)}
                            placeholder={region === "sa" ? "Riyadh" : "Dubai"}
                            required
                            className="h-12"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="state">
                            {region === "uae" ? (language === "ar" ? "الإمارة *" : "Emirate *") : region === "sa" ? (language === "ar" ? "المنطقة *" : "Province *") : (language === "ar" ? "الولاية *" : "State *")}
                          </Label>
                          <Input
                            id="state"
                            value={formData.state}
                            onChange={(e) => handleInputChange("state", e.target.value)}
                            placeholder={region === "uae" ? "Dubai" : region === "sa" ? "Riyadh Province" : "NSW"}
                            required
                            className="h-12"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="zip_code">{language === "ar" ? "الرمز البريدي *" : "Postal Code *"}</Label>
                          <Input
                            id="zip_code"
                            value={formData.zip_code}
                            onChange={(e) => handleInputChange("zip_code", e.target.value)}
                            placeholder={isMiddleEast ? "00000" : "2000"}
                            required
                            className="h-12"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="country">{language === "ar" ? "الدولة *" : "Country *"}</Label>
                        <Input
                          id="country"
                          value={formData.country}
                          onChange={(e) => handleInputChange("country", e.target.value)}
                          placeholder="United Arab Emirates"
                          required
                          className="h-12"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="order_notes">
                          {language === "ar" ? "ملاحظات الطلب (اختياري)" : "Order Notes (Optional)"}
                        </Label>
                        <Textarea
                          id="order_notes"
                          value={formData.order_notes}
                          onChange={(e) => handleInputChange("order_notes", e.target.value)}
                          placeholder={language === "ar" ? "أي تعليمات خاصة..." : "Any special instructions..."}
                          className="h-24"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Submit via WhatsApp */}
                  <div>
                    <button
                      type="submit"
                      className="w-full bg-[#25D366] hover:bg-[#1FB857] disabled:bg-gray-400 disabled:cursor-not-allowed text-white h-14 md:h-16 text-base md:text-lg font-bold rounded-md shadow-lg transition-all flex items-center justify-center gap-3"
                    >
                      <MessageCircle className="w-5 h-5 md:w-6 md:h-6" />
                      {language === "ar" ? "إرسال الطلب عبر واتساب" : "Send Order via WhatsApp"}
                    </button>
                    <div className="mt-2 flex items-center justify-center gap-2 text-xs text-gray-500">
                      <MessageCircle className="w-3 h-3" />
                      <span>{language === "ar" ? "يفتح محادثة واتساب مع تفاصيل طلبك" : "Opens a WhatsApp chat pre-filled with your order details"}</span>
                    </div>
                  </div>

                  {/* Important Notice */}
                  <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm font-semibold text-red-900">
                        <strong>{language === "ar" ? "تنبيه:" : "Important:"}</strong>{" "}
                        {language === "ar"
                          ? "يتطلب هذا المنتج كمبروسر هواء (غير مضمّن) بضغط لا يقل عن 8 بار وسعة 8-10.5 جالون."
                          : "This product requires an air compressor with minimum 8 Bar pressure and 8-10.5 gallon capacity. Air compressor is not included."}
                      </p>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="shadow-2xl sticky top-24">
              <CardHeader className="bg-gray-900 text-white p-6">
                <CardTitle className="flex items-center gap-3 text-2xl font-black">
                  <ShoppingCart className="w-6 h-6" />
                  {language === "ar" ? "ملخص الطلب" : "Order Summary"}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="flex gap-4">
                  <img
                    src="https://res.cloudinary.com/dilx8zm2p/image/upload/f_auto,q_auto,w_200/v1759783025/1_iatrwm.jpg"
                    alt="LCTX Tire Changer"
                    className="w-20 h-20 object-cover rounded"
                    width="200"
                    height="200"
                    loading="lazy"
                  />
                  <div>
                    <h3 className="font-bold">{language === "ar" ? "مبدّل إطارات LCTX" : "LCTX Tyre Changer"}</h3>
                    <p className="text-sm text-gray-600">{language === "ar" ? "هوائي، 12-21 بوصة" : "Air-Powered, 12-21\""}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between py-4 border-y">
                  <span>{language === "ar" ? "الكمية" : "Quantity"}</span>
                  <div className="flex items-center gap-3">
                    <motion.button
                      type="button"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-8 h-8 border rounded hover:bg-gray-100 dark:hover:bg-gray-800 dark:border-gray-600 select-none"
                      whileTap={{ scale: 0.9 }}
                      transition={{ duration: 0.1 }}
                    >
                      -
                    </motion.button>
                    <motion.span
                      key={quantity}
                      initial={{ scale: 1.2, color: "#25D366" }}
                      animate={{ scale: 1, color: "#000000" }}
                      transition={{ duration: 0.15 }}
                      className="font-bold w-8 text-center dark:text-white"
                    >
                      {quantity}
                    </motion.span>
                    <motion.button
                      type="button"
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-8 h-8 border rounded hover:bg-gray-100 dark:hover:bg-gray-800 dark:border-gray-600 select-none"
                      whileTap={{ scale: 0.9 }}
                      transition={{ duration: 0.1 }}
                    >
                      +
                    </motion.button>
                  </div>
                </div>

                <div className="space-y-2">
                  {summaryLines.map((line, i) => (
                    <div key={i} className={`flex justify-between text-gray-600 ${line.className || ""}`}>
                      <span>{line.label}</span>
                      <span>{line.value}</span>
                    </div>
                  ))}
                  <div className="border-t pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold">
                        {isMiddleEast ? (language === "ar" ? "الإجمالي (شامل الضريبة)" : "Total (incl. VAT)") : (language === "ar" ? "الإجمالي (شامل GST)" : "Total (inc. GST)")}
                      </span>
                      <span className="text-2xl font-black text-[#25D366]">{formatLocalPrice(totalAmount)}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="font-bold text-green-800 text-sm">{t("cod_payment")}</span>
                  </div>
                  <p className="text-xs text-green-700">
                    {language === "ar"
                      ? "ادفع نقدًا عند استلام طردك. لا حاجة لبطاقة ائتمان."
                      : "Pay with cash when your parcel arrives. No credit card needed."}
                  </p>
                </div>

                <div className="flex flex-col gap-2 text-xs text-gray-500 pt-2">
                  <div className="flex items-center gap-2"><Truck className="w-4 h-4" /> {language === "ar" ? "شحن مجاني" : "Free Shipping"}</div>
                  <div className="flex items-center gap-2"><Shield className="w-4 h-4" /> {language === "ar" ? "ضمان 30 يومًا" : "30-Day Money Back"}</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}